import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { ConversationDTO, ShopType } from "@easyshop/shared";
import { SHOP_TYPE_LABELS, TASK_STATUS_LABELS } from "@easyshop/shared";
import { Card, Empty, ErrorState, FilterChips, Loading, TypeBadge, cx, taskStatusClass } from "@/components/ui";
import { Composer, ThreadMessages } from "@/components/messaging/Thread";
import { useBoutiqueConversationId, useConversations, useTaskTracking } from "@/lib/queries";
import { useSession } from "@/hooks/useSession";
import { useUi } from "@/hooks/useUi";
import { dayMonth, stamp, time } from "@/lib/format";

type Tab = "conversations" | "suivi";
type ConvFilter = "tous" | "non_lus" | "taches" | "retard";

const CONV_FILTERS: { key: ConvFilter; label: string }[] = [
  { key: "tous", label: "Tous" },
  { key: "non_lus", label: "Non lus" },
  { key: "taches", label: "Tâches ouvertes" },
  { key: "retard", label: "En retard" },
];

function useIsNarrow(maxWidth = 767) {
  const query = `(max-width: ${maxWidth}px)`;
  const [narrow, setNarrow] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setNarrow(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [query]);
  return narrow;
}

const typeAvatar = (t: ShopType) => (t === "INTEGRATED" ? "bg-positive-bg text-positive" : "bg-franchise-bg text-franchise");

/** « Messages & tâches » : conversations par boutique + suivi d'exécution des tâches. */
export function MessagesPage() {
  const { can } = useSession();
  const { openCompose } = useUi();
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get("tab") === "suivi" ? "suivi" : "conversations";

  const setTab = (t: Tab) => {
    const next = new URLSearchParams(params);
    if (t === "suivi") next.set("tab", "suivi");
    else next.delete("tab");
    setParams(next, { replace: true });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-[10px]">
        <div className="flex gap-[6px]">
          {(
            [
              { key: "conversations", label: "Conversations" },
              { key: "suivi", label: "Suivi des tâches" },
            ] as const
          ).map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={cx(
                "rounded-full px-4 py-[9px] text-[13px] font-semibold",
                tab === t.key ? "bg-accent text-white" : "bg-transparent text-[oklch(0.5_0.012_150)] hover:text-ink"
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        {can("messages.send") && (
          <button
            type="button"
            onClick={() => openCompose({ kind: can("tasks.create") ? "tache" : "message" })}
            className="rounded-full bg-accent px-[18px] py-[9px] text-[13px] font-bold text-white hover:bg-accent-strong"
          >
            + Nouveau message / tâche
          </button>
        )}
      </div>
      {tab === "conversations" ? <ConversationsTab /> : <TrackingTab />}
    </div>
  );
}

// --- Conversations ------------------------------------------------------------------

function ConversationsTab() {
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState<ConvFilter>(() => (params.get("filtre") === "retard" ? "retard" : "tous"));
  const list = useConversations(filter);
  const all = useConversations("tous");
  const narrow = useIsNarrow();

  const convParam = params.get("conversation");
  const boutiqueParam = params.get("boutique");
  const byBoutique = useBoutiqueConversationId(convParam ? undefined : boutiqueParam ?? undefined);
  const [showThread, setShowThread] = useState(Boolean(convParam || boutiqueParam));

  const items = list.data ?? [];
  const selectedId = convParam ?? byBoutique.data?.id ?? (boutiqueParam ? undefined : items[0]?.id);
  const selected: ConversationDTO | undefined = useMemo(
    () => [...(list.data ?? []), ...(all.data ?? [])].find((c) => c.id === selectedId),
    [list.data, all.data, selectedId]
  );

  const select = (id: string) => {
    const next = new URLSearchParams(params);
    next.delete("boutique");
    next.set("conversation", id);
    setParams(next, { replace: true });
    setShowThread(true);
  };

  const listCard = (
    <Card pad="p-0" className="flex max-h-[680px] w-full flex-shrink-0 flex-col overflow-hidden md:w-[340px]">
      <div className="border-b border-line-soft p-3">
        <FilterChips options={CONV_FILTERS} value={filter} onChange={setFilter} />
      </div>
      <div className="flex-1 overflow-auto">
        {list.isLoading ? (
          <Loading />
        ) : list.error ? (
          <ErrorState error={list.error} className="m-3" />
        ) : items.length === 0 ? (
          <Empty>Aucune conversation.</Empty>
        ) : (
          items.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => select(c.id)}
              className={cx(
                "flex w-full items-start gap-[10px] border-b border-line-row px-[14px] py-3 text-left",
                c.id === selectedId ? "bg-accent-row" : "bg-white hover:bg-[oklch(0.985_0.004_145)]"
              )}
            >
              <div className={cx("flex h-[34px] w-[34px] flex-shrink-0 items-center justify-center rounded-full text-[12px] font-bold", typeAvatar(c.boutique.type))}>
                {c.boutique.city.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex justify-between gap-[6px]">
                  <span className="truncate text-[13px] font-bold">{c.boutique.city}</span>
                  <span className="flex-shrink-0 text-[10.5px] text-faint">{stamp(c.lastMessageAt)}</span>
                </div>
                <div className="mt-[2px] truncate text-[12px] text-muted">{c.lastMessage ?? "Aucun message"}</div>
              </div>
              {c.unread > 0 && (
                <span className="flex h-4 min-w-[16px] flex-shrink-0 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold text-white">
                  {c.unread}
                </span>
              )}
            </button>
          ))
        )}
      </div>
    </Card>
  );

  const threadCard = (
    <Card className="flex h-[680px] max-h-[calc(100dvh-160px)] min-w-0 flex-1 flex-col gap-4 md:min-w-[320px]" pad="px-4 py-[18px] md:px-[26px] md:py-[22px]">
      {selectedId ? (
        <>
          <div className="flex items-center gap-[10px] border-b border-line-soft pb-[14px]">
            {narrow && (
              <button type="button" onClick={() => setShowThread(false)} className="-ml-1 px-1 text-[16px] text-muted" aria-label="Retour">
                ←
              </button>
            )}
            <span className="truncate text-[15px] font-bold">{selected?.boutique.city ?? "Conversation"}</span>
            {selected && <TypeBadge type={selected.boutique.type} />}
          </div>
          <div className="min-h-0 flex-1 overflow-auto rounded-card-sm bg-page px-3 py-4 md:px-4">
            <ThreadMessages conversationId={selectedId} />
          </div>
          <Composer conversationId={selectedId} />
        </>
      ) : boutiqueParam && byBoutique.isLoading ? (
        <Loading />
      ) : byBoutique.error ? (
        <ErrorState error={byBoutique.error} />
      ) : (
        <Empty>Sélectionnez une conversation.</Empty>
      )}
    </Card>
  );

  if (narrow) return showThread && selectedId ? threadCard : listCard;
  return (
    <div className="flex gap-4">
      {listCard}
      {threadCard}
    </div>
  );
}

// --- Suivi des tâches ---------------------------------------------------------------

function fmtStamp(iso: string | null) {
  if (!iso) return "—";
  return `${dayMonth(iso)}, ${time(iso)}`;
}

function TrackingTab() {
  const q = useTaskTracking();
  const [taskId, setTaskId] = useState<string | null>(null);

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorState error={q.error} />;
  const tasks = q.data ?? [];
  if (tasks.length === 0) return <Card><Empty>Aucune tâche envoyée pour le moment.</Empty></Card>;
  const t = tasks.find((x) => x.id === taskId) ?? tasks[0];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-[10px]">
        <span className="text-[12px] font-semibold text-muted">Tâche</span>
        <select
          value={t.id}
          onChange={(e) => setTaskId(e.target.value)}
          className="min-w-0 max-w-full flex-1 rounded-full border border-line bg-white px-[14px] py-2 text-[12.5px] font-semibold sm:max-w-[420px] sm:flex-none"
        >
          {tasks.map((x) => (
            <option key={x.id} value={x.id}>
              {x.title} — {dayMonth(x.createdAt)}
            </option>
          ))}
        </select>
      </div>

      <div className="text-[13.5px] font-bold">Suivi — {t.title}</div>
      <div className="flex flex-wrap gap-4">
        <div className="rounded-card-md border border-line bg-white px-[22px] py-4">
          <div className="text-[12px] text-muted">Taux d'exécution</div>
          <div className="mt-1 font-display text-[26px] font-extrabold tabular">{Math.round(t.executionRate)} %</div>
        </div>
        {t.dueAt && (
          <div className="rounded-card-md border border-line bg-white px-[22px] py-4">
            <div className="text-[12px] text-muted">Échéance</div>
            <div className="mt-1 font-display text-[26px] font-extrabold">{dayMonth(t.dueAt)}</div>
          </div>
        )}
      </div>

      <Card pad="p-0" className="overflow-x-auto">
        <div className="min-w-[600px]">
          <div className="grid grid-cols-[1fr_110px_130px_130px_110px] border-b border-line-soft px-5 py-3 text-[11px] font-bold text-muted-2">
            <span>BOUTIQUE</span>
            <span>TYPE</span>
            <span>STATUT</span>
            <span>VU LE</span>
            <span>FAIT LE</span>
          </div>
          {t.rows.map((r) => (
            <div
              key={r.assignmentId}
              className="grid grid-cols-[1fr_110px_130px_130px_110px] items-center border-b border-line-row px-5 py-[14px] text-[13px] last:border-b-0"
            >
              <span className="truncate pr-2 font-semibold">{r.city}</span>
              <span className="text-[11px] text-muted-2">{SHOP_TYPE_LABELS[r.type]}</span>
              <span>
                <span className={cx("inline-block rounded-full px-[10px] py-[3px] text-[11px] font-bold", taskStatusClass(r.status, r.late))}>
                  {TASK_STATUS_LABELS[r.status]}
                </span>
              </span>
              <span className="text-muted-2">{fmtStamp(r.seenAt)}</span>
              <span className="text-muted-2">{fmtStamp(r.doneAt)}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
